import type { ActionIcon, ActionRun } from "@afar-research/agent-content/actions";
import {
  listThreadActions,
  resolveActionAppOwnedSkillInvocations,
  threadActionSkillNames,
  resolveThreadAction,
  skillActionSelection,
  threadActionAvailability,
  threadActionLabel,
  type ThreadActionContext,
} from "@afar-research/afar";
import type {
  ProviderSkillInvocation,
  ServerProviderSkill,
  ThreadActionSelection,
} from "@afar-research/contracts";

/**
 * One Action as the composer offers it: in the `+` menu, under `/`, and on the
 * empty thread's starter rows. Unavailable Actions stay listed with the reason
 * rather than disappearing, so the user learns why they cannot run here.
 */
export interface ComposerActionEntry {
  readonly selection: ThreadActionSelection;
  readonly label: string;
  readonly description: string;
  /** Null for an Action a project or user skill declared: clients draw a generic icon. */
  readonly icon: ActionIcon | null;
  readonly group: "verb" | "housekeeping" | "custom";
  readonly run: ActionRun;
  readonly disabledReason: string | null;
}

/** A skill that declared itself an Action, as the composer's provider catalog reports it. */
export type ComposerCustomActionSkill = Pick<ServerProviderSkill, "name" | "description"> & {
  readonly actionLabel: string;
  readonly shortDescription?: string | undefined;
};

/** The provider skills that declared themselves Actions, in catalog order. */
export function customActionSkills(
  skills: ReadonlyArray<ServerProviderSkill>,
): ReadonlyArray<ComposerCustomActionSkill> {
  const seen = new Set<string>();
  const result: ComposerCustomActionSkill[] = [];
  for (const skill of skills) {
    if (skill.actionLabel === undefined || skill.ownership === "app") continue;
    if (skill.enabled === false || seen.has(skill.name)) continue;
    seen.add(skill.name);
    result.push({
      name: skill.name,
      actionLabel: skill.actionLabel,
      description: skill.description,
      shortDescription: skill.shortDescription,
    });
  }
  return result;
}

/** Every Action the composer offers here: the verbs, then housekeeping, then custom Actions. */
export function listComposerActionEntries(input: {
  readonly context: ThreadActionContext;
  readonly customSkills: ReadonlyArray<ComposerCustomActionSkill>;
}): ReadonlyArray<ComposerActionEntry> {
  const builtIn = (["verb", "housekeeping"] as const).flatMap((kind) =>
    listThreadActions(kind).map((definition): ComposerActionEntry => {
      const availability = threadActionAvailability(definition, input.context);
      return {
        selection: { id: definition.id },
        label: definition.label,
        description: definition.description,
        icon: definition.icon,
        group: kind,
        run: definition.run,
        disabledReason: availability.available ? null : availability.reason,
      };
    }),
  );
  const custom = input.customSkills.map(
    (skill): ComposerActionEntry => ({
      selection: skillActionSelection(skill.name),
      label: skill.actionLabel,
      description: skill.shortDescription ?? skill.description ?? `Runs the ${skill.name} skill`,
      icon: null,
      group: "custom",
      run: "chip",
      disabledReason: null,
    }),
  );
  return [...builtIn, ...custom];
}

/**
 * The entries a `/` query reaches, best match first: a label that starts with
 * the query, then one that contains it, then a description that does. An empty
 * query lists the verbs only, so `/` alone stays a short list.
 */
export function matchComposerActionEntries(
  entries: ReadonlyArray<ComposerActionEntry>,
  query: string,
): ReadonlyArray<ComposerActionEntry> {
  const normalized = query.trim().replace(/^\/+/u, "").toLowerCase();
  if (normalized.length === 0) return entries.filter((entry) => entry.group === "verb");
  const ranked: Array<{ entry: ComposerActionEntry; score: number; index: number }> = [];
  entries.forEach((entry, index) => {
    const label = entry.label.toLowerCase();
    const id = entry.selection.id.toLowerCase();
    const score =
      label.startsWith(normalized) || id.startsWith(normalized)
        ? 0
        : label.includes(normalized)
          ? 1
          : entry.description.toLowerCase().includes(normalized)
            ? 2
            : null;
    if (score !== null) ranked.push({ entry, score, index });
  });
  return ranked
    .toSorted((left, right) => left.score - right.score || left.index - right.index)
    .map((candidate) => candidate.entry);
}

/** What the composer's chip shows for the Action it holds. */
export interface ComposerActionChipModel {
  readonly label: string;
  readonly icon: ActionIcon | null;
  readonly variants: ReadonlyArray<{ readonly id: string; readonly label: string }>;
  readonly variant: string | null;
  readonly disabledReason: string | null;
}

/**
 * The chip for a held Action, or null when the selection no longer names one
 * (a custom Action whose skill is gone still shows, by its skill name).
 */
export function composerActionChip(
  selection: ThreadActionSelection | null,
  context: ThreadActionContext,
  customSkills: ReadonlyArray<ComposerCustomActionSkill> = [],
): ComposerActionChipModel | null {
  const resolved = resolveThreadAction(selection);
  if (resolved === null) return null;
  if (resolved.type === "skill") {
    const declared = customSkills.find((skill) => skill.name === resolved.skillName);
    return {
      label: declared?.actionLabel ?? resolved.label,
      icon: null,
      variants: [],
      variant: null,
      disabledReason: null,
    };
  }
  const availability = threadActionAvailability(resolved.definition, context);
  return {
    // The variant has its own dropdown on the chip, so the label names the Action alone.
    label:
      resolved.definition.variants === undefined
        ? threadActionLabel(resolved)
        : resolved.definition.label,
    icon: resolved.definition.icon,
    variants: (resolved.definition.variants ?? []).map((variant) => ({
      id: variant.id,
      label: variant.label,
    })),
    variant: resolved.selection.variant ?? null,
    disabledReason: availability.available ? null : availability.reason,
  };
}

/**
 * The selection a pick starts from. An Action with variants starts on its
 * first one so the chip is complete the moment it appears; the dropdown on the
 * chip changes it.
 */
export function initialActionSelection(selection: ThreadActionSelection): ThreadActionSelection {
  const resolved = resolveThreadAction(selection);
  if (resolved?.type !== "built-in" || selection.variant !== undefined) return selection;
  const first = resolved.definition.variants?.[0];
  return first === undefined ? selection : { id: selection.id, variant: first.id };
}

/**
 * The skill invocations a send with this Action carries, so the provider runs
 * its Skill Bundles natively rather than reading `$name` tokens. A built-in
 * Action binds App-owned bundles; a custom Action binds the project or user
 * skill that declared it. A bundle this provider's catalog lacks is left out:
 * the prompt keeps its token.
 */
export function resolveActionSkillInvocations(
  skills: ReadonlyArray<ServerProviderSkill>,
  selection: ThreadActionSelection | null,
): ReadonlyArray<ProviderSkillInvocation> {
  const resolved = resolveThreadAction(selection);
  if (resolved === null) return [];
  if (resolved.type === "built-in") return resolveActionAppOwnedSkillInvocations(skills, selection);
  return threadActionSkillNames(resolved).flatMap((name) => {
    const skill = skills.find(
      (candidate) => candidate.name === name && candidate.enabled && candidate.usable !== false,
    );
    return skill === undefined
      ? []
      : [{ name: skill.name, path: skill.path, promptStart: 0, promptEnd: name.length + 1 }];
  });
}
